import React, { useMemo, useState } from "react";
import { Loader2, Upload, FileText, CheckCircle2, AlertTriangle, Eye, Save } from "lucide-react";
import { toast } from "sonner";
import api from "../console/api";
import { errMsg } from "../hq/hqUtils";

/* /admin/bulk-import , paste CSV → preview → commit.
   Applies your business rules automatically:
   • Mother's Day box → 6 HQ picks, customer chooses
   • Mystery beginner/hobby/addict → blind 5/8/10 box, no customer choice
   • Seeds / Apparel → Logged Historical Orders, no stock allocation */

const AUBERGINE  = "#5A4565";
const PLUM       = "#6F4A7A";
const GOLD       = "#C9A268";
const PARCH_EDGE = "#E0D1C8";

const SAMPLE = `customer_name,email,phone,address,order_type,notes,total
Jane Doe,jane@example.com,021 000 111,12 Bloom Lane Auckland,mothers-day,Wrap as gift,80
John Smith,john@example.com,021 222 333,7 Posy Road Wellington,mystery-hobby,No wedding pinks please,55
Mary Bell,mary@example.com,,4 Garden Place Christchurch,mystery-addict,,75
Sue Lee,sue@example.com,,9 Tubers Cres Hamilton,seeds,3 packets,18
Tim Park,tim@example.com,,15 Frock St Tauranga,apparel,XL shirt,40`;

export default function AdminBulkImport() {
  const [csvText, setCsvText] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);

  const canImport = useMemo(() => preview && (preview.summary?.console_orders + preview.summary?.historical_orders) > 0, [preview]);

  const doPreview = async () => {
    if (!csvText.trim()) { toast.error("Paste your CSV first."); return; }
    setPreviewing(true);
    setResult(null);
    try {
      const r = await api.post("/admin/bulk-import", { csv: csvText, dry_run: true });
      setPreview(r.data);
      if ((r.data.errors || []).length) {
        toast.warning(`${r.data.errors.length} row(s) need attention.`);
      } else {
        toast.success(`Ready to import ${r.data.summary.console_orders + r.data.summary.historical_orders} orders.`);
      }
    } catch (e) {
      toast.error(errMsg(e, "Couldn't preview."));
    } finally { setPreviewing(false); }
  };

  const doImport = async () => {
    if (!canImport) return;
    if (!window.confirm(`Import ${preview.summary.console_orders} pipeline orders + ${preview.summary.historical_orders} historical entries? This will create real rows. Continue?`)) return;
    setImporting(true);
    try {
      const r = await api.post("/admin/bulk-import", { csv: csvText, dry_run: false });
      setResult(r.data);
      toast.success(`Imported. ${r.data.inserted_console_orders?.length || 0} console + ${r.data.inserted_historical_orders?.length || 0} historical.`);
      setCsvText("");
      setPreview(null);
    } catch (e) {
      toast.error(errMsg(e, "Import failed."));
    } finally { setImporting(false); }
  };

  return (
    <div data-testid="admin-bulk-import" className="space-y-6 max-w-5xl">
      <header>
        <div className="text-[10px] tracking-[0.45em] uppercase font-semibold" style={{ color: PLUM }}>✦ Bulk Import</div>
        <h1 className="font-serif text-4xl mt-1.5" style={{ color: AUBERGINE }}>Load Orders in Bulk</h1>
        <p className="font-serif italic mt-1.5 text-[#6E5849] max-w-3xl text-sm leading-relaxed">
          Paste a CSV. We&apos;ll apply the business rules: <strong>Mother&apos;s Day boxes</strong> get 6 customer picks,{" "}
          <strong>mystery boxes</strong> are full surprise (5/8/10 by tier), and <strong>seeds &amp; apparel</strong>{" "}
          land in the historical orders tab without inventory.
        </p>
      </header>

      <div className="p-4 rounded border" style={{ borderColor: PARCH_EDGE, backgroundColor: "#FBF6E9" }}>
        <div className="flex items-start gap-2 text-sm" style={{ color: AUBERGINE }}>
          <FileText className="w-4 h-4 mt-0.5 shrink-0" style={{ color: GOLD }} />
          <div>
            <strong>Expected columns</strong> (header row required; spaces/case OK): <code className="text-xs">customer_name, email, phone, address, order_type, notes, total</code><br />
            <strong>order_type</strong> accepts: <code className="text-xs">mothers-day · mystery-beginner · mystery-hobby · mystery-addict · seeds · apparel · tubers</code>
            <button
              type="button"
              onClick={() => setCsvText(SAMPLE)}
              className="ml-2 underline text-[11px] tracking-[0.2em] uppercase font-semibold"
              style={{ color: PLUM }}
            >
              Load sample
            </button>
          </div>
        </div>
      </div>

      <textarea
        value={csvText}
        onChange={(e) => setCsvText(e.target.value)}
        rows={12}
        placeholder="Paste your CSV here…"
        data-testid="bulk-csv-input"
        className="w-full px-3 py-3 text-sm border rounded font-mono leading-relaxed"
        style={{ borderColor: PARCH_EDGE, backgroundColor: "white" }}
      />

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={doPreview}
          disabled={previewing || !csvText.trim()}
          data-testid="bulk-preview-btn"
          className="px-5 py-2.5 rounded text-xs tracking-[0.25em] uppercase font-semibold inline-flex items-center gap-1.5 transition disabled:opacity-50"
          style={{ backgroundColor: "white", border: `1px solid ${AUBERGINE}`, color: AUBERGINE }}
        >
          {previewing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}
          Preview
        </button>
        <button
          type="button"
          onClick={doImport}
          disabled={importing || !canImport}
          data-testid="bulk-import-btn"
          className="px-5 py-2.5 rounded text-xs tracking-[0.25em] uppercase font-semibold inline-flex items-center gap-1.5 transition disabled:opacity-50"
          style={{ backgroundColor: AUBERGINE, color: "white" }}
        >
          {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
          Commit Import
        </button>
      </div>

      {preview && (
        <section className="bg-white border rounded p-4" style={{ borderColor: PARCH_EDGE }} data-testid="bulk-preview-panel">
          <div className="text-[10px] tracking-[0.3em] uppercase font-semibold mb-2" style={{ color: PLUM }}>Preview</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm" style={{ color: AUBERGINE }}>
            <div><strong>{preview.summary.console_orders}</strong> pipeline orders (Awaiting Payment)</div>
            <div><strong>{preview.summary.historical_orders}</strong> historical entries (Seeds &amp; Apparel)</div>
            <div><strong>{preview.summary.skipped}</strong> skipped (missing data)</div>
          </div>

          {(preview.errors || []).length > 0 && (
            <div className="mt-3 p-3 rounded border text-xs" style={{ borderColor: "#E8B5A2", backgroundColor: "#FFF4F0", color: "#A33030" }}>
              <div className="font-semibold mb-1 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Issues</div>
              <ul className="list-disc pl-5">
                {preview.errors.map((e, i) => <li key={i}>Row {e.row}: {e.issue}</li>)}
              </ul>
            </div>
          )}

          <details className="mt-3 text-xs">
            <summary className="cursor-pointer font-semibold" style={{ color: PLUM }}>Per-row plan ({preview.preview?.length || 0})</summary>
            <ul className="mt-2 space-y-0.5 font-mono">
              {(preview.preview || []).map((p) => (
                <li key={p.row} className="grid grid-cols-[40px_120px_140px_60px_1fr] gap-2" style={{ color: AUBERGINE }}>
                  <span>#{p.row}</span>
                  <span>{p.target}</span>
                  <span>{p.kind}{p.tier ? `/${p.tier}` : ""}</span>
                  <span>{p.expected_picks ? `${p.picks}/${p.expected_picks} picks` : "-"}</span>
                  <span className="truncate">{p.customer}</span>
                </li>
              ))}
            </ul>
          </details>
        </section>
      )}

      {result && (
        <section className="bg-[#EBF3E5] border rounded p-4" style={{ borderColor: "#C8DCB6", color: "#2F4A2A" }} data-testid="bulk-result-panel">
          <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> <strong>Import complete.</strong></div>
          <div className="text-sm mt-1">
            {result.inserted_console_orders?.length || 0} pipeline orders + {result.inserted_historical_orders?.length || 0} historical entries created.
          </div>
          {(result.errors || []).length > 0 && (
            <div className="mt-2 text-xs italic">{result.errors.length} row(s) failed , scroll up to fix and retry.</div>
          )}
        </section>
      )}
    </div>
  );
}
